import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  LinearProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  IconButton,
} from '@mui/material';
import { 
  Visibility as ViewIcon,
  Download as DownloadIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';

const OnboardingStep = ({ 
  selectedEmployee,
  onStepComplete,
  onStepIncomplete,
  isCompleted
}) => {
  const navigate = useNavigate();
  const { tokenManager } = useAuth();
  const [userDetails, setUserDetails] = useState(null);
  const [onboardingProgress, setOnboardingProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formVersions, setFormVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Fetch user details
  const fetchUserDetails = useCallback(async () => {
    if (!selectedEmployee?.userId) return;

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/users/${selectedEmployee.userId}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUserDetails(userData);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch user details');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployee?.userId]);

  // Fetch onboarding progress
  const fetchOnboardingProgress = useCallback(async () => {
    if (!selectedEmployee?.userId) return;

    try {
      const response = await fetch(`${API_URL}/onboarding/admin/user-details/${selectedEmployee.userId}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setOnboardingProgress(result.data);
      }
    } catch (err) {
      console.error('Error fetching onboarding progress:', err);
    }
  }, [selectedEmployee?.userId]);

  // Fetch form versions
  const fetchFormVersions = useCallback(async () => {
    if (!selectedEmployee?.userId) return;

    setVersionsLoading(true);
    try {
      const response = await fetch(`${API_URL}/onboarding-form-versions/user/${selectedEmployee.userId}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setFormVersions(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching form versions:', err);
    } finally {
      setVersionsLoading(false);
    }
  }, [selectedEmployee?.userId]);

  useEffect(() => {
    if (selectedEmployee?.userId) {
      fetchUserDetails();
      fetchOnboardingProgress();
      fetchFormVersions();
    }
  }, [selectedEmployee?.userId, fetchUserDetails, fetchOnboardingProgress, fetchFormVersions]);

  const handleViewForm = () => {
    if (selectedEmployee?.userId) {
      navigate(`/onboarding-form/${selectedEmployee.userId}`);
    }
  };

  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      onStepComplete();
    } else {
      onStepIncomplete();
    }
  };

  const handleDownloadVersion = async (versionId, versionNumber, employeeName) => {
    try {
      const downloadUrl = `${API_URL}/onboarding-form-versions/download/${versionId}`;
      const token = tokenManager.getToken();
      
      console.log('🔍 Download attempt:', {
        versionId,
        versionNumber,
        employeeName,
        downloadUrl,
        hasToken: !!token,
        tokenLength: token ? token.length : 0
      });
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      // Fetch the file with authentication headers
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        let errorMessage = `Download failed: ${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse the error response, use the default message
        }
        throw new Error(errorMessage);
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${employeeName}_onboarding_form_v${versionNumber}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log(`✅ Excel file downloaded: ${filename}`);
      
    } catch (error) {
      console.error('❌ Error downloading version:', error);
      alert(`Failed to download Excel file: ${error.message}`);
    }
  };

  const handleRefresh = () => {
    fetchUserDetails();
    fetchOnboardingProgress();
    fetchFormVersions();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'warning';
      case 'Pending': return 'default';
      default: return 'default';
    }
  };



  if (!selectedEmployee) {
    return (
      <Alert severity="info">
        Please select an employee first to manage their onboarding form.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* User Information Card */}
      {userDetails ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Employee Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">
                  {userDetails?.fullName || `${userDetails?.firstName || ''} ${userDetails?.lastName || ''}`.trim() || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">
                  {userDetails?.email || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Department
                </Typography>
                <Typography variant="body1">
                  {userDetails?.department || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Job Title
                </Typography>
                <Typography variant="body1">
                  {userDetails?.jobTitle || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Alert severity="info">
              No user information available for this employee.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Progress Overview */}
      {onboardingProgress ? (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Onboarding Progress
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2">
                Progress: {onboardingProgress.completedSteps || 0} / {onboardingProgress.totalSteps || 0} steps
              </Typography>
              <Chip 
                label={onboardingProgress.status || 'Pending'} 
                color={getStatusColor(onboardingProgress.status || 'Pending')}
                size="small"
              />
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={onboardingProgress.progressPercentage || 0} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {Math.round(onboardingProgress.progressPercentage || 0)}% Complete
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Steps Breakdown */}
          <Typography variant="subtitle1" gutterBottom>
            Steps Breakdown
          </Typography>
          <Grid container spacing={1}>
            {onboardingProgress.steps && onboardingProgress.steps.length > 0 ? (
              onboardingProgress.steps.map((step, index) => (
                <Grid item xs={12} key={index}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    p: 1,
                    borderRadius: 1,
                    bgcolor: step.completed ? 'success.light' : 'grey.100'
                  }}>
                    <Typography variant="body2">
                      {step.module || `Step ${index + 1}`}
                    </Typography>
                    <Chip 
                      label={step.completed ? 'Completed' : 'Pending'} 
                      color={step.completed ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Alert severity="info">
                  No onboarding steps found for this employee.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Alert severity="info">
            No onboarding progress data available for this employee.
          </Alert>
        </Paper>
      )}

      {/* Form Version History */}
      {selectedEmployee?.userId && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            Form Version History
          </Typography>
          
          {versionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : formVersions.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Version</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formVersions.map((version) => (
                    <TableRow key={version._id}>
                      <TableCell>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => handleDownloadVersion(version._id, version.versionNumber, version.formData?.employeeName)}
                          sx={{
                            textDecoration: 'none',
                            fontWeight: 'bold',
                            color: 'primary.main',
                            '&:hover': {
                              textDecoration: 'underline',
                              color: 'primary.dark'
                            }
                          }}
                        >
                          v{version.versionNumber}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(version.createdAt).toLocaleDateString()} {new Date(version.createdAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadVersion(version._id, version.versionNumber, version.formData?.employeeName)}
                          title="Download Excel File"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No form versions found. Versions will be created automatically when the employee saves or updates their form.
            </Alert>
          )}
        </Paper>
      )}

      {/* Onboarding Form Actions */}
      {selectedEmployee?.userId && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Onboarding Form Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ViewIcon />}
              onClick={handleViewForm}
            >
              View Form
            </Button>
            <Button
              variant="contained"
              onClick={handleRefresh}
              disabled={loading || versionsLoading}
            >
              Refresh Details
            </Button>
          </Box>
        </Box>
      )}

      {/* Mark as Complete */}
      <Box sx={{ mt: 3 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isCompleted}
              onChange={handleCheckboxChange}
              color="primary"
            />
          }
          label="Mark as Complete"
        />
      </Box>

      {!selectedEmployee?.userId && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No user account linked to this employee. Onboarding form cannot be accessed.
        </Alert>
      )}
    </Box>
  );
};

export default OnboardingStep; 